import {
    FHIRQuestionnaire,
    QuestionnaireItem,
    AnswerOption,
    Coding,
} from '../types/fhir'
import { logger } from 'app/logging/Logger'

/**
 * Metadata for a questionnaire item that determines how it should be rendered
 */
export interface QuestionnaireItemMetadata {
    linkId: string
    type: string
    text: string
    required: boolean
    readOnly: boolean
    repeats: boolean

    // For choice/open-choice types
    answerValueSet?: string
    answerOptions?: AnswerOption[]

    // Coding information
    code?: Coding[]

    // For nested items (groups)
    items?: QuestionnaireItemMetadata[]
}

/**
 * Parse FHIR Questionnaire and create a lookup map by linkId
 */
export class QuestionnaireParser {
    private itemMap: Map<string, QuestionnaireItemMetadata>
    private questionnaire: FHIRQuestionnaire

    constructor(questionnaire: FHIRQuestionnaire) {
        this.questionnaire = questionnaire
        this.itemMap = new Map()
        this.parseItems(questionnaire.item)
    }

    /**
     * Get metadata for a specific item by linkId
     */
    getItem(linkId: string): QuestionnaireItemMetadata | undefined {
        return this.itemMap.get(linkId)
    }

    /**
     * Get all items as a map
     */
    getAllItems(): Map<string, QuestionnaireItemMetadata> {
        return this.itemMap
    }

    /**
     * Get all ValueSet URLs referenced in the questionnaire
     */
    getValueSetUrls(): string[] {
        const urls: string[] = []

        this.itemMap.forEach((item) => {
            if (item.answerValueSet) {
                urls.push(item.answerValueSet)
            }
        })

        // Remove duplicates. Array.from, not [...new Set()]: the app compiles
        // to ES5 without downlevelIteration, where spreading a Set yields [].
        return Array.from(new Set(urls))
    }

    /**
     * Check if an item is editable (not a group or display, not readOnly)
     */
    isEditable(linkId: string): boolean {
        const item = this.getItem(linkId)
        if (!item) return false

        return (
            item.type !== 'group' && item.type !== 'display' && !item.readOnly
        )
    }

    /**
     * Check if an item requires constrained input (has ValueSet or options)
     */
    isConstrained(linkId: string): boolean {
        const item = this.getItem(linkId)
        if (!item) return false

        return !!(item.answerValueSet || item.answerOptions)
    }

    /**
     * Recursively parse questionnaire items and build the map
     */
    private parseItems(
        items: QuestionnaireItem[],
        _parentPath: string = ''
    ): void {
        items.forEach((item) => {
            const metadata = this.parseItem(item)
            this.itemMap.set(item.linkId, metadata)

            logger.debug('Parsed questionnaire item', {
                action: 'questionnaire_item_parsed',
                metadata: {
                    linkId: item.linkId,
                    type: item.type,
                    hasValueSet: !!item.answerValueSet,
                    hasOptions: !!item.answerOption,
                    repeats: item.repeats,
                },
            })

            // Recursively parse nested items
            if (item.item) {
                this.parseItems(item.item, item.linkId)
            }
        })
    }

    /**
     * Parse a single questionnaire item into metadata
     */
    private parseItem(item: QuestionnaireItem): QuestionnaireItemMetadata {
        // Check for hidden extension
        const isHidden = item.extension?.some(
            (ext) =>
                ext.url ===
                    'http://hl7.org/fhir/StructureDefinition/questionnaire-hidden' &&
                ext.valueBoolean === true
        )

        return {
            linkId: item.linkId,
            type: item.type,
            text: item.text,
            required: item.required || item.text?.includes('*') || false,
            readOnly: item.readOnly || isHidden || false,
            repeats: item.repeats || false,
            answerValueSet: item.answerValueSet,
            answerOptions: item.answerOption,
            code: item.code,
            items: item.item?.map((child) => this.parseItem(child)),
        }
    }
}

/**
 * Parse a Questionnaire JSON string into a QuestionnaireParser
 */
export function parseQuestionnaire(
    questionnaireJson: string
): QuestionnaireParser {
    try {
        const questionnaire: FHIRQuestionnaire = JSON.parse(questionnaireJson)

        logger.debug('Parsing questionnaire', {
            action: 'questionnaire_parse_start',
            metadata: {
                id: questionnaire.id,
                name: questionnaire.name,
                itemCount: questionnaire.item?.length || 0,
            },
        })

        const parser = new QuestionnaireParser(questionnaire)

        logger.debug('Questionnaire parsed successfully', {
            action: 'questionnaire_parse_success',
            metadata: {
                id: questionnaire.id,
                totalItems: parser.getAllItems().size,
                valueSetCount: parser.getValueSetUrls().length,
            },
        })

        return parser
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Failed to parse questionnaire', err, {
            action: 'questionnaire_parse_error',
            metadata: { error: err.message },
        })
        throw err
    }
}
